import { useState } from 'react';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Rocket, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ClientFormModal } from './ClientFormModal';
import { ClientConfigDialog } from './ClientConfigDialog';
import { OnboardingPanel } from '@/features/onboarding/OnboardingPanel';
import { useClients, useClientMutations } from './useClients';
import { useRole } from '@/hooks/useRole';
import type { Client } from '@/types/database';

const PACKAGE_LABELS: Record<string, string> = {
  essencial: 'Essencial',
  avancado: 'Avançado',
  premium: 'Premium',
};

const STATUS_BADGE: Record<Client['status'], { label: string; className: string }> = {
  lead: { label: 'Lead', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  onboarding: { label: 'Onboarding', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  ativo: { label: 'Ativo', className: 'bg-green-100 text-green-700 border-green-200' },
  suspenso: { label: 'Suspenso', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  cancelado: { label: 'Cancelado', className: 'bg-red-100 text-red-700 border-red-200' },
};

function StatusBadge({ status }: { status: Client['status'] }) {
  const { label, className } = STATUS_BADGE[status];
  return (
    <Badge variant="outline" className={`text-xs font-medium ${className}`}>
      {label}
    </Badge>
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '—';
  }
}

export function ClientsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [onboardingClient, setOnboardingClient] = useState<Client | null>(null);
  const [configClient, setConfigClient] = useState<Client | null>(null);
  const { isManager } = useRole();

  const filters: Parameters<typeof useClients>[0] = {};
  if (search) filters.search = search;
  if (statusFilter) filters.status = statusFilter;
  const { data: clients = [], isLoading } = useClients(filters);
  const { remove } = useClientMutations();

  const openCreate = () => {
    setEditingClient(null);
    setModalOpen(true);
  };
  const openEdit = (c: Client) => {
    setEditingClient(c);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditingClient(null);
  };

  const confirmDelete = async () => {
    if (!deletingClient) return;
    await remove.mutateAsync(deletingClient.id);
    setDeletingClient(null);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            {clients.length} cliente{clients.length !== 1 ? 's' : ''} encontrado
            {clients.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Novo cliente
        </Button>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder="Buscar por nome…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter || '__all__'}
          onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}
        >
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os status</SelectItem>
            {(Object.keys(STATUS_BADGE) as Client['status'][]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_BADGE[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <div className="rounded-xl border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="hidden md:table-cell">Responsável</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Pacote</TableHead>
              <TableHead className="hidden lg:table-cell">Início</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  Carregando clientes…
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  {search || statusFilter
                    ? 'Nenhum cliente encontrado com esses filtros.'
                    : 'Nenhum cliente cadastrado ainda.'}
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id} className="hover:bg-secondary/40">
                  <TableCell>
                    <div className="font-medium text-foreground">{client.name}</div>
                    {client.email && (
                      <div className="text-xs text-muted-foreground">{client.email}</div>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                    {client.responsible_name ?? '—'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={client.status} />
                  </TableCell>
                  <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
                    {client.package ? (PACKAGE_LABELS[client.package] ?? client.package) : '—'}
                  </TableCell>
                  <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
                    {formatDate(client.contract_start)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(client)}>
                          <Pencil size={14} className="mr-2" />
                          Editar
                        </DropdownMenuItem>
                        {(client.status === 'lead' ||
                          client.status === 'onboarding' ||
                          client.status === 'ativo') && (
                          <DropdownMenuItem onClick={() => setOnboardingClient(client)}>
                            <Rocket size={14} className="mr-2" />
                            Onboarding
                          </DropdownMenuItem>
                        )}
                        {isManager && (
                          <DropdownMenuItem onClick={() => setConfigClient(client)}>
                            <Settings size={14} className="mr-2" />
                            Configurações
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => setDeletingClient(client)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ClientFormModal open={modalOpen} onClose={closeModal} client={editingClient} />

      {onboardingClient && (
        <OnboardingPanel
          open={!!onboardingClient}
          onOpenChange={(o) => !o && setOnboardingClient(null)}
          clientId={onboardingClient.id}
          clientName={onboardingClient.name}
        />
      )}

      {configClient && (
        <ClientConfigDialog
          open={!!configClient}
          onOpenChange={(o) => !o && setConfigClient(null)}
          clientId={configClient.id}
          clientName={configClient.name}
        />
      )}

      <AlertDialog
        open={Boolean(deletingClient)}
        onOpenChange={(v) => !v && setDeletingClient(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{deletingClient?.name}</strong>? Esta ação não
              pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
